/**
 * ContactAudreyBubble — Sprint 92g
 *
 * Bulle contact permanente accessible depuis TOUTES les pages (public + portal
 * + admin), à côté de la bulle IA. Ouvre un mini-popup avec :
 *   • Bouton WhatsApp (message pré-rempli, ouvre wa.me)
 *   • Bouton Appel direct (tel:)
 *   • Bouton Prendre RDV (Google Calendar Audrey)
 *   • Mention des langues supportées (FR/EN/ES/PT)
 *
 * Positionnée en bottom-right, décalée à gauche de la bulle IA pour ne pas
 * les faire se chevaucher.
 */
import { useState } from "react";
import { MessageCircle, Phone, CalendarDays, X, Headset } from "lucide-react";

const WA_NUMBER = "33660235665";
const WA_MESSAGE = "Bonjour Audrey, je viens du site La WineTech et j'ai une question : ";
const TEL_NUMBER = "+33660235665";
const CALENDAR_URL = "https://calendar.app.google/6ydHqvM2QqV1iJZF6";

export default function ContactAudreyBubble() {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* Floating button — stacked ABOVE the AI bubble on the right side */}
      <button
        onClick={() => setOpen(!open)}
        aria-label="Contacter Audrey"
        className="fixed bottom-24 right-5 z-[60] flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-2xl ring-4 ring-white/40 transition-transform hover:scale-105 hover:bg-emerald-600"
        data-testid="audrey-bubble"
        title="Contacter Audrey directement"
      >
        {open ? <X className="h-6 w-6" /> : <Headset className="h-6 w-6" />}
      </button>

      {/* Popup card — anchored above the Audrey bubble */}
      {open && (
        <div
          className="fixed bottom-44 right-5 z-[60] w-[300px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-3xl border border-neutral-200 bg-white shadow-2xl"
          data-testid="audrey-popup"
        >
          {/* Header */}
          <div className="flex items-center gap-3 bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 text-white">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
              <Headset className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold">Contactez Audrey</div>
              <div className="text-[10px] opacity-90">Community Manager · FR/EN/ES/PT</div>
            </div>
          </div>

          {/* Body */}
          <div className="space-y-2 p-4">
            <p className="mb-3 text-[11px] text-neutral-600">
              Une question, un doute, une demande urgente ? Audrey vous répond directement.
            </p>

            <a
              href={`https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(WA_MESSAGE)}`}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center gap-3 rounded-2xl bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-800 transition-colors hover:bg-emerald-100"
              data-testid="audrey-whatsapp"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white">
                <MessageCircle className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-[13px]">WhatsApp</div>
                <div className="text-[10px] font-normal text-emerald-600">Message rapide · réponse rapide</div>
              </div>
            </a>

            <a
              href={`tel:${TEL_NUMBER}`}
              className="flex w-full items-center gap-3 rounded-2xl bg-sky-50 px-3 py-2.5 text-sm font-semibold text-sky-800 transition-colors hover:bg-sky-100"
              data-testid="audrey-call"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-500 text-white">
                <Phone className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-[13px]">Appel direct</div>
                <div className="text-[10px] font-normal text-sky-600">+33 6 60 23 56 65</div>
              </div>
            </a>

            <a
              href={CALENDAR_URL}
              target="_blank"
              rel="noreferrer"
              className="flex w-full items-center gap-3 rounded-2xl bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-900 transition-colors hover:bg-amber-100"
              data-testid="audrey-calendar"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500 text-white">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div className="flex-1">
                <div className="text-[13px]">Prendre rendez-vous</div>
                <div className="text-[10px] font-normal text-amber-700">Réservez un créneau dans son agenda</div>
              </div>
            </a>
          </div>
        </div>
      )}
    </>
  );
}
